import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";
import { config as appConfig } from "../../../config";
import { AppRoutes } from "../../../routes/AppRoutes";
import { isAppSource } from "../../../helpers/getSourceFromUrl";
import {
  useGetCurrentSubscriptionsOptionsQuery,
  useEditOptionMutation,
  useEditSubscriptionMutation
} from "../../../rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";
import { SubscriptionItem, OptionItem } from "../../../types/rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";
import { useGetMeanQuery } from "../../../rtk/mean/mean";

import Text from "../../atoms/Text/Text";
import Header from "../../templates/Header/Header";
import SpinnerLoader from "../../atoms/SpinnerLoader/SpinnerLoader";
import ManageSubscriptionsOptionsList from "../../organisms/ManageSubscriptionsOptionsList/ManageSubscriptionsOptionsList";
import PopIn from "../../molecules/PopIn/PopIn";

const ManageSubscriptions = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();

  const { data: subsOptsData, isLoading: subsOptsLoading, error: subsOptsError } = useGetCurrentSubscriptionsOptionsQuery();
  const [editOption, { isLoading: isLoadingOption }] = useEditOptionMutation();
  const [editSubscription, { isLoading: isLoadingSubscription }] = useEditSubscriptionMutation();
  const { data: meanData, isLoading: meanLoading } = useGetMeanQuery();

  const isDisabled = isLoadingOption || isLoadingSubscription;
  const [paymentMeanPopIn, setPaymentMeanPopIn] = useState<{
    open: boolean;
    title?: string;
    subtitle?: string;
  }>({ open: false });

  const handleActivate = async (item: SubscriptionItem | OptionItem) => {
    if (isDisabled) return;

    if (meanData?.default_payment_mean == null || meanData?.default_payment_mean?.valid === false || meanData?.default_payment_mean?.expired === true) {
      setPaymentMeanPopIn({
        open: true,
        title: item.type === "subscription"
          ? t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.TITLE.SUBSCRIPTION")
          : t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.TITLE.OPTION"),
        subtitle: meanData?.default_payment_mean == null
          ? t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.SUBTITLE.MISSING")
          : meanData.default_payment_mean.expired === true
            ? t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.SUBTITLE.EXPIRED")
            : t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.SUBTITLE.ERRORS"),
      });
      return;
    }

    try {
      if (item.type === "subscription") {
        await editSubscription({
          renewal_active: true
        }).unwrap();
      } else {
        await editOption({
          option_code: item.code,
          renewal_active: true
        }).unwrap();
      }
    } catch (error) {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'error' } });
    }
  };

  const handleEditButton = (item: SubscriptionItem | OptionItem) => {
    if (isDisabled) return;
    const route = item.type === "option"
      ? AppRoutes.MANAGE_SUBSCRIPTIONS_CANCEL_CONFIRM
      : AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT;

    navigate(route, {
      state: { ...item, tracker_name: subsOptsData?.tracker_name }
    });
  };

  return (
    <>
      {!isAppSource() && (
        <Header
          headerStep={{ context: "manage" }}
          title={t("subscription:MANAGE_PAGE.TITLE")}
          goBack={() => window.location.href = appConfig.REACT_APP_FE_BASE_URL}
        />
      )}
      <Box className="ManageSubscriptions">
        {(subsOptsLoading) && <SpinnerLoader />}

        {!subsOptsLoading && subsOptsError && (
          <Text children="Error while retrieving subscriptions." />
        )}

        {!subsOptsLoading && !meanLoading && subsOptsData && (
          <ManageSubscriptionsOptionsList
            data={subsOptsData}
            onActivate={handleActivate}
            onEdit={handleEditButton}
            isDisabled={isDisabled}
          />
        )}
      </Box>
      <PopIn
        isOpen={paymentMeanPopIn.open}
        onClose={() => setPaymentMeanPopIn({ open: false })}
        title={paymentMeanPopIn.title ?? ""}
        description={paymentMeanPopIn.subtitle}
        buttonLabel={t("subscription:MANAGE_PAGE.INVALID_PAYMENT_MEAN_POPIN.BUTTON")}
        onButtonClick={() => {
          const params = new URLSearchParams({ origin: "manage-subscriptions" });
          navigate(`${AppRoutes.MANAGE_PAYMENTS}?${params}`);
          console.log("Navigating to manage payments due to invalid payment mean");
        }}
      />
    </>
  );
};

export default ManageSubscriptions;
